import type { QueryClient } from '@tanstack/react-query';
import { otpustitVseArendy } from '../aktualnost/reestr';
import { ubratChernovikiINamereniya } from '../obrashcheniya/chernoviki';

/**
 * СМЕНА ПОЛЬЗОВАТЕЛЯ В БРАУЗЕРЕ (`s719`, общая приёмка 06.10.2026: «„Войти как“ должно на 100 %
 * воспроизводить возможности менеджера… при смене пользователя очищать недоступные данные,
 * запросы и подписки»).
 *
 * Права, данные и филиалы сервер считает с учётки на каждом запросе — там подмены нет. Но в
 * браузере от прежнего человека оставалось своё:
 *  · личные настройки в хранилище (выбранный филиал, раздел меню, фильтры отчётов) — под учёткой
 *    менеджера панель открывалась с филиалом и разделом владельца;
 *  · в ДРУГИХ открытых вкладках — данные, запросы и опросы прежнего человека: «Войти как» в одной
 *    вкладке меняет сессию для всех, а соседняя продолжала показывать то, что менеджеру не открыто;
 *  · аренды редактирования прежнего человека: отпустить их после смены уже нельзя — сессия чужая.
 *
 * Правило одно на вход, выход и «Войти как»: человек в браузере опознаётся меткой «кто сидит за
 * сессией : под какой учёткой». Метка сменилась — личное убирается, кэш данных очищается, вкладка
 * перечитывается с домашней страницы новой учётки.
 */
const METKA = 'opsmind:kto';
const ZAPAS = 'opsmind:lichnoe-do-vhoda-kak';

/** Личные настройки человека в `localStorage`: рабочий контекст, а не устройство. */
const LICHNOE_LOCAL = ['opsmind_current_filial_id', 's603.filial', 'opsmind-ads-filters', 'opsmind_analytics_filters', 'opsmind-direct-filters', 'opsmind_stol'];
/** То же в `sessionStorage` — по началу ключа: раздел меню, незавершённое намерение по сделке. */
const LICHNOE_SESSION = ['opsmind:menyu:', 'amo-deal-intent:'];

export interface Kto {
  id: number;
  impersonated_by?: { id: number } | null;
}

/** Метка человека: «сидящий за сессией : учётка». */
export function metkaCheloveka(kto: Kto): string {
  return `${kto.impersonated_by?.id ?? kto.id}:${kto.id}`;
}

function bezopasno<T>(delo: () => T, zapasnoe: T): T {
  try {
    return delo();
  } catch {
    // Хранилище закрыто настройками браузера — личного там и нет.
    return zapasnoe;
  }
}

function lichnyeKlyuchiSessii(): string[] {
  const klyuchi: string[] = [];
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const k = sessionStorage.key(i);
    if (k !== null && LICHNOE_SESSION.some((nachalo) => k.startsWith(nachalo))) klyuchi.push(k);
  }

  return klyuchi;
}

/** Убрать личные настройки прежнего человека. */
export function ubratLichnoe(): void {
  // Текст клиентке и ключ незавершённой отправки — не настройки устройства:
  // другому человеку не достаются ни из sessionStorage, ни из памяти модуля.
  ubratChernovikiINamereniya();
  bezopasno(() => {
    for (const k of LICHNOE_LOCAL) localStorage.removeItem(k);
    for (const k of lichnyeKlyuchiSessii()) sessionStorage.removeItem(k);
  }, undefined);
}

/** Отложить своё на время «Войти как» — вернётся при возврате к себе в этой же вкладке. */
function otlozhitLichnoe(sidit: string): void {
  bezopasno(() => {
    const svoyo: Record<string, string> = {};
    for (const k of LICHNOE_LOCAL) {
      const z = localStorage.getItem(k);
      if (z !== null) svoyo[k] = z;
    }
    sessionStorage.setItem(ZAPAS, JSON.stringify({ sidit, svoyo }));
  }, undefined);
  ubratLichnoe();
}

function vernutLichnoe(sidit: string): void {
  ubratLichnoe();
  bezopasno(() => {
    const syroe = sessionStorage.getItem(ZAPAS);
    sessionStorage.removeItem(ZAPAS);
    if (syroe === null) return;

    const zapas = JSON.parse(syroe) as { sidit?: string; svoyo?: Record<string, string> };
    if (zapas.sidit !== sidit) return;
    for (const [k, z] of Object.entries(zapas.svoyo ?? {})) localStorage.setItem(k, z);
  }, undefined);
}

/**
 * Привести хранилище браузера к человеку, который сейчас вошёл. Возвращает `true`, если человек
 * сменился (личное прежнего убрано).
 */
export function privestiHranilishche(kto: Kto): boolean {
  const novaya = metkaCheloveka(kto);
  const prezhnyaya = bezopasno(() => localStorage.getItem(METKA), null);

  if (prezhnyaya === novaya) return false;

  const [sidit, uchetka] = novaya.split(':');
  const [sidel, bylaUchetka] = (prezhnyaya ?? ':').split(':');

  if (prezhnyaya !== null && sidit === sidel && sidel === bylaUchetka && uchetka !== sidit) {
    // Тот же человек вошёл под чужой учёткой: своё откладывается.
    otlozhitLichnoe(sidit);
  } else if (prezhnyaya !== null && sidit === sidel && sidit === uchetka) {
    // Вернулся к себе: следы учётки убираются, своё возвращается.
    vernutLichnoe(sidit);
  } else if (prezhnyaya !== null) {
    // Другой человек (или другая учётка под «Войти как»): личное прежнего убирается.
    ubratLichnoe();
    bezopasno(() => sessionStorage.removeItem(ZAPAS), undefined);
  }

  bezopasno(() => localStorage.setItem(METKA, novaya), undefined);

  return prezhnyaya !== null;
}

/**
 * ПЕРЕД сменой сессии (вход под учёткой, возврат, выход): аренды редактирования отпускаются, пока
 * сессия ещё прежнего человека, а идущие запросы отменяются — их ответы новому человеку не нужны.
 */
export async function peredSmenoy(qc: QueryClient): Promise<() => void> {
  const zavershit = await otpustitVseArendy();
  try {
    await qc.cancelQueries();
    return zavershit;
  } catch (error) {
    zavershit();
    throw error;
  }
}

/** Барьер принадлежит конкретной смене и живёт до ответа auth или его отказа. */
export async function priSmenePolzovatelya<T>(qc: QueryClient, deystvie: () => Promise<T>): Promise<T> {
  const zavershit = await peredSmenoy(qc);
  try {
    return await deystvie();
  } finally {
    zavershit();
  }
}

/** Данные прежнего человека из памяти вкладки — долой. */
export function ochistitDannye(qc: QueryClient): void {
  void qc.cancelQueries();
  qc.clear();
}

export const KLYUCH_METKI = METKA;

/** Перечитать вкладку с домашней страницы новой учётки (вынесено, чтобы проверка могла подменить). */
export const perehod = {
  naDom: (): void => {
    window.location.assign('/');
  },
};
